<template>
  <div class="divSmallMarginBottom marketToolBar column smallerGap">
    <!-- Single row on desktop, two rows on mobile -->
    <div class="filters-top-row" :class="{ 'search-active': searchActive }">
      <!-- Post/Commit buttons — stay in the search row -->
      <div class="filters-main-row">
        <!-- Search container -->
        <div class="search-container">
          <input
            v-model="localSearchQuery"
            placeholder="Search..."
            @focus="onSearchFocus"
            @blur="onSearchBlur"
          />
          <svg class="search-icon compactImg">
            <use href="@/assets/icons/iconset.svg#lens-compact"></use>
          </svg>
          <button
            class="filter-toggle-btn"
            :class="{ active: filtersVisible }"
            @click="filtersVisible = !filtersVisible"
            aria-label="Toggle filters"
          >
            <svg class="compactImg">
              <use :href="`${iconsetPath}#filter_compact`"></use>
            </svg>
          </button>
        </div>
      </div>

      <!-- Filter controls — inline on desktop, second row on mobile -->
      <div class="filter-controls" :class="{ visible: filtersVisible }">
        <div class="type-filters-container">
          <HelpHint
            v-for="filter in typeFilters"
            :key="filter.type"
            :message="filter.label"
          >
            <template #default="{ ariaDescribedby }">
              <UnitButton
                variant="text"
                size="micro"
                icon-only
                class="type-filter-item"
                :class="{ active: filter.active }"
                :aria-label="filter.label"
                :aria-describedby="ariaDescribedby"
                :aria-pressed="String(filter.active)"
                @click="toggleTypeFilter(filter.type)"
              >
                <span class="icon-container">
                  <svg class="filter-type-icon compactImg" aria-hidden="true">
                    <use :href="`${iconsetPath}#${filter.icon}`"></use>
                  </svg>
                </span>
              </UnitButton>
            </template>
          </HelpHint>
        </div>

        <!-- Wishlist icon-only toggle -->
        <HelpHint v-if="!hideFavourites" message="Favourites">
          <template #default="{ ariaDescribedby }">
            <UnitButton
              variant="text"
              size="micro"
              icon-only
              class="wishlistIconButton"
              :class="{ active: showWishlist }"
              :aria-pressed="showWishlist ? 'true' : 'false'"
              aria-label="Toggle favourites list"
              :aria-describedby="ariaDescribedby"
              @click="toggleWishlist"
            >
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#star_compact"></use>
              </svg>
            </UnitButton>
          </template>
        </HelpHint>

        <!-- Location Filter (Commented out for now) -->
        <!-- <div class="location-filter-inline-wrapper">
          <LocationFilterComponent
            @update:locationFilter="handleLocationFilterUpdate"
            class="location-filter-component"
          />
        </div> -->
      </div>

      <div
        class="postActionSlotWrapper"
        ref="postWrapperRef"
        @click="onPostActionClick"
        :data-has-selling-slot="!!$slots['selling-action']"
        :data-show-selling-action-prop="showSellingAction"
        :data-panel-render-condition="
          !!($slots['selling-action'] && showSellingAction)
        "
      >
        <slot name="post-action"></slot>
        <!-- Selling action panel — always rendered so overlays work; dropdown visibility controlled by SellingAction's showDropdown prop -->
        <div
          v-if="$slots['selling-action']"
          class="sellingActionPanel"
          :class="[panelDirectionClass, { hidden: !showSellingAction }]"
          :data-direction="panelDirection"
          ref="panelRef"
          role="dialog"
          aria-modal="false"
        >
          <slot name="selling-action"></slot>
        </div>
      </div>
    </div>

    <!-- Closed economy modal -->
    <ConfirmDialog
      v-if="showClosedEconomyModal"
      :visible="showClosedEconomyModal"
      title="Closed Economy"
      message="Global marketplace is disabled for this organisation."
      :details="[
        'Switch to an organisation with an open economy to view global items.',
        'Trading not allowed: administrators can change economy settings in Admin Dashboard > Overview.',
      ]"
      tone="neutral"
      placement="center"
      :show-cancel="false"
      confirm-label="OK"
      @confirm="showClosedEconomyModal = false"
      @close="showClosedEconomyModal = false"
    >
      <template #actions="{ confirm }">
        <UnitButton v-haptic @click="confirm">OK</UnitButton>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script>
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  computed,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import { feedTypeSelection } from "@/utils/feedPresentation";
import { db, doc, updateDoc, getDoc, setDoc } from "@/services/legacyBrowserPersistenceBoundary";
import { isOrgContextFeatureEnabled } from "@/config/runtimeConfig";
import { auth } from "@/firebase";
import { toast } from "vue3-toastify";
import HelpHint from "@/components/GenericComponents/HelpHint.vue";
import LocationFilterComponent from "./LocationFilterComponent.vue";
import ConfirmDialog from "@/components/GenericComponents/ConfirmDialog.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import iconsetPath from "@/assets/icons/iconset.svg";

export default {
  name: "FiltersComponent",
  components: {
    HelpHint,
    LocationFilterComponent,
    ConfirmDialog,
    UnitButton,
  },

  props: {
    categories: {
      type: Array,
      required: true,
    },
    // Add prop for parent component to pass initial filter type
    initialFilterType: {
      type: String,
      // Empty default means the complete supported feed.
      default: "",
    },
    orgMode: {
      type: Boolean,
      default: false,
    },
    orgName: {
      type: String,
      default: "",
    },
    // Whether current organisation allows trades; if false block switching to global
    isTradeAllowed: {
      type: Boolean,
      default: true,
    },
    // Control selling action visibility from parent (passed through chain)
    showSellingAction: {
      type: Boolean,
      default: false,
    },
    // Hide the favourites toggle (e.g. on profile / organisation views)
    hideFavourites: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:searchQuery",
    "update:filterType",
    "update:sortOrder",
    "update:selectedCategory",
    "update:locationFilter",
    "update:orgMode",
    "blocked-global-attempt",
    // Custom wishlist toggle event used by parent to react to favourites panel visibility
    "wishlistToggled",
    // Search focus state for collapsing action buttons
    "search-active",
  ],

  setup(props, { emit, slots }) {
    const store = useStore();
    // Dynamic panel direction logic
    const postWrapperRef = ref(null);
    const panelRef = ref(null);
    const panelDirection = ref("down"); // 'down' or 'up'

    // Debounce utility (simple trailing; no leading) for resize/scroll
    const debounce = (fn, wait = 80) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    };

    const measureAndDecide = () => {
      const wrapperEl = postWrapperRef.value;
      if (!wrapperEl) return;
      const rect = wrapperEl.getBoundingClientRect();
      const viewportH =
        window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportH - rect.bottom;
      const spaceAbove = rect.top;
      let panelH = panelRef.value ? panelRef.value.offsetHeight : 0;
      if (!panelH) {
        panelH = Math.min(560, Math.round(viewportH * 0.7));
      }
      const nearViewportBottom = spaceBelow < 120; // slightly larger margin than before
      let shouldOpenUp =
        (spaceBelow < panelH && spaceAbove > spaceBelow) || nearViewportBottom;
      panelDirection.value = shouldOpenUp ? "up" : "down";
    };

    const multiPassCompute = (passes = 2) => {
      if (!props.showSellingAction) return;
      measureAndDecide();
      if (passes > 0) {
        requestAnimationFrame(() => multiPassCompute(passes - 1));
      }
    };

    const computeDirection = () => multiPassCompute(2);

    const debouncedRecompute = debounce(() => computeDirection(), 90);

    const panelDirectionClass = computed(
      () => `direction-${panelDirection.value}`
    );

    const handleResize = () => {
      debouncedRecompute();
    };
    const handleScroll = () => {
      debouncedRecompute();
    };

    watch(
      () => props.showSellingAction,
      async (open) => {
        if (open) {
          await nextTick();
          // Double-check slot still present before computing
          if (!slots["selling-action"]) {
            console.warn(
              "[FiltersComponent] Panel open requested but selling-action slot is missing at render time"
            );
          }
          computeDirection();
          window.addEventListener("resize", handleResize, { passive: true });
          window.addEventListener("scroll", handleScroll, { passive: true });
        } else {
          window.removeEventListener("resize", handleResize);
          window.removeEventListener("scroll", handleScroll);
        }
      }
    );

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    });
    // Wishlist toggle (global UI state)
    const showWishlist = computed(() => store.getters["UIState/showWishlist"]);
    const toggleWishlist = () => {
      store.commit("UIState/TOGGLE_WISHLIST");
      emit("wishlistToggled", showWishlist.value);
    };

    const localSearchQuery = ref("");
    const searchFocused = ref(false);
    const searchCollapsed = ref(false); // true = buttons show icon-only
    const searchActive = computed(() => searchCollapsed.value);

    const onSearchFocus = () => {
      searchFocused.value = true;
      searchCollapsed.value = true;
      emit("search-active", true);
    };
    const onSearchBlur = () => {
      searchFocused.value = false;
      // Keep collapsed while there's text in the search field
      if (!localSearchQuery.value) {
        searchCollapsed.value = false;
        emit("search-active", false);
      }
    };
    const onPostActionClick = () => {
      // User clicked a post/commit button — restore full button labels
      searchCollapsed.value = false;
      emit("search-active", false);
    };

    const localSortOrder = ref("timestamp_desc");
    const selectedCategory = ref("");
    const filtersVisible = ref(false);
    const currentUser = computed(() => store.state.user);
    const currentOrgUID = computed(() => store.getters.currentOrgUID);
    // Local org/global mode state mirrored from prop
    // Initialise local org mode: if economy closed force org mode true
    const localOrgMode = ref(props.isTradeAllowed ? props.orgMode : true);
    const showClosedEconomyModal = ref(false);
    watch(
      () => props.orgMode,
      (nv) => {
        if (nv !== localOrgMode.value) localOrgMode.value = nv;
      }
    );
    const onOrgToggle = (val) => {
      if (val === localOrgMode.value) return; // no-op
      // Economy closed & attempted switch to global
      if (!props.isTradeAllowed && val === false) {
        // Immediately re-emit the original state to keep child checkbox visually locked
        queueMicrotask(() => emit("update:orgMode", true));
        localOrgMode.value = true;
        onBlockedGlobalAttempt();
        console.warn(
          "[FiltersComponent] Fallback guard prevented global scope (allowTrades=false)."
        );
        return;
      }
      // Enabling org mode requires an org UID
      if (val && !currentOrgUID.value) {
        console.warn(
          "[FiltersComponent] Cannot enable org mode — no currentOrgUID yet"
        );
        localOrgMode.value = false;
        emit("update:orgMode", false);
        return;
      }
      localOrgMode.value = val;
      emit("update:orgMode", val);
      const storeOrgOnly = store.getters["UIState/showOwnOrganisationOnly"];
      if (val !== storeOrgOnly) {
        if (store._actions["UIState/toggleShowOwnOrganisationOnly"]) {
          store.dispatch("UIState/toggleShowOwnOrganisationOnly");
        } else if (store._actions["UIState/setShowOwnOrganisationOnly"]) {
          store.dispatch("UIState/setShowOwnOrganisationOnly", val);
        }
      }
      console.debug("[FiltersComponent] Org mode toggled", {
        to: val,
        orgUID: currentOrgUID.value,
      });
      toast.info(
        val ? "Switched to organisation scope" : "Switched to global scope",
        { autoClose: 1600, position: "bottom-center" }
      );
    };

    const onBlockedGlobalAttempt = () => {
      showClosedEconomyModal.value = true;
      emit("blocked-global-attempt");
      toast.warn("Global marketplace disabled for this organisation", {
        autoClose: 2200,
        position: "bottom-center",
      });
    };

    // Define the type filters with toggle state
    const typeFilters = ref([
      {
        type: "product",
        icon: "product-compact",
        label: "Products",
        active: false,
      },
      {
        type: "service",
        icon: "service-compact",
        label: "Services",
        active: false,
      },
      {
        type: "media_image",
        icon: "image-compact",
        label: "Images",
        active: false,
      },
      {
        type: "media_video",
        icon: "video-compact",
        label: "Videos",
        active: false,
      },
      {
        type: "media_audio",
        icon: "audio-compact",
        label: "Audio",
        active: false,
      },
      { type: "docs", icon: "file-compact", label: "Docs", active: false },
      { type: "nft", icon: "nft_compact", label: "NFTs", active: false },
    ]);

    // Function to toggle a specific filter type
    const toggleTypeFilter = async (type) => {
      const filter = typeFilters.value.find((f) => f.type === type);
      if (filter) {
        filter.active = !filter.active;
        updateFilterTypeEmit();

        if (auth.currentUser) {
          await saveUserFilterPreferences();
        }
      }
    };

    // Function to emit the combined filter types
    const updateFilterTypeEmit = () => {
      emit("update:filterType", feedTypeSelection(typeFilters.value));
    };

    // Function to save user filter preferences to Firestore
    const saveUserFilterPreferences = async () => {
      try {
        if (!auth.currentUser || isOrgContextFeatureEnabled) return;

        const userId = auth.currentUser.uid;
        const userPrefsRef = doc(db, "users", userId);
        const userPrefsSnapshot = await getDoc(userPrefsRef);

        if (userPrefsSnapshot.exists()) {
          // Update existing preferences
          await updateDoc(userPrefsRef, {
            "preferences.marketplace.typeFilters": typeFilters.value.map(
              (filter) => ({
                type: filter.type,
                active: filter.active,
              })
            ),
            "preferences.marketplace.sortOrder": localSortOrder.value,
            "preferences.marketplace.selectedCategory": selectedCategory.value,
          });
        } else {
          // Create new preferences document
          await setDoc(
            userPrefsRef,
            {
              preferences: {
                marketplace: {
                  typeFilters: typeFilters.value.map((filter) => ({
                    type: filter.type,
                    active: filter.active,
                  })),
                  sortOrder: localSortOrder.value,
                  selectedCategory: selectedCategory.value,
                },
              },
            },
            { merge: true }
          );
        }

        console.log("User marketplace preferences saved successfully");
      } catch (error) {
        console.error("Error saving user marketplace preferences:", error);
      }
    };

    // Function to load user filter preferences from Firestore
    const loadUserFilterPreferences = async () => {
      try {
        if (!auth.currentUser || isOrgContextFeatureEnabled) return;

        const userId = auth.currentUser.uid;
        const userPrefsRef = doc(db, "users", userId);
        const userPrefsSnapshot = await getDoc(userPrefsRef);

        if (userPrefsSnapshot.exists()) {
          const data = userPrefsSnapshot.data();

          if (data.preferences?.marketplace?.typeFilters) {
            // Update local typeFilters with saved preferences
            data.preferences.marketplace.typeFilters.forEach((savedFilter) => {
              const filter = typeFilters.value.find(
                (f) => f.type === savedFilter.type
              );
              if (filter) {
                filter.active = savedFilter.active;
              }
            });

            // Update UI with saved preferences
            updateFilterTypeEmit();
          }

          // Set other saved preferences
          if (data.preferences?.marketplace?.sortOrder) {
            localSortOrder.value = data.preferences.marketplace.sortOrder;
          }

          if (data.preferences?.marketplace?.selectedCategory) {
            selectedCategory.value =
              data.preferences.marketplace.selectedCategory;
            emit("update:selectedCategory", selectedCategory.value);
          }
        }
      } catch (error) {
        console.error("Error loading user marketplace preferences:", error);
      }
    };

    const toggleFilters = () => {
      filtersVisible.value = !filtersVisible.value;
    };

    const onCategoryChange = async () => {
      emit("update:selectedCategory", selectedCategory.value);

      // Save user preferences if logged in
      if (auth.currentUser) {
        await saveUserFilterPreferences();
      }
    };

    // Handle location filter updates
    const handleLocationFilterUpdate = (locationFilter) => {
      emit("update:locationFilter", locationFilter);
    };

    // Watch for changes in search query
    watch(localSearchQuery, (newValue) => {
      emit("update:searchQuery", newValue);
    });

    // Watch for changes in sort order
    watch(localSortOrder, async (newValue) => {
      emit("update:sortOrder", newValue);

      // Save user preferences if logged in
      if (auth.currentUser) {
        await saveUserFilterPreferences();
      }
    });

    // Watch for changes in user authentication state
    watch(
      () => auth.currentUser,
      (newUser) => {
        if (newUser) {
          loadUserFilterPreferences();
        } else {
          // Reset to defaults if logged out
          typeFilters.value.forEach((filter) => {
            filter.active = true;
          });
          updateFilterTypeEmit();
        }
      }
    );

    // Load user preferences on component mount
    onMounted(async () => {
      // Apply the initial filter type from props
      const hasExplicitTypeFilter = Boolean(props.initialFilterType);
      const defaultTypes = props.initialFilterType
        ? props.initialFilterType
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      // A blank filter means the complete feed. The __none__ sentinel remains
      // available only when the caller explicitly selected no content types.
      typeFilters.value.forEach((filter) => {
        filter.active = hasExplicitTypeFilter
          ? defaultTypes.includes(filter.type)
          : true;
      });

      // TEMPORARILY DISABLE saved preferences to ensure complete filter is used
      // Load user preferences only if logged in, but don't override the initial filter
      if (auth.currentUser) {
        // const userId = auth.currentUser.uid;
        // const userPrefsRef = doc(db, "users", userId);
        // const userPrefsSnapshot = await getDoc(userPrefsRef);

        // if (userPrefsSnapshot.exists()) {
        //   const data = userPrefsSnapshot.data();

        //   // Only apply saved preferences if they exist, otherwise keep the initial filter
        //   if (data.preferences?.marketplace?.typeFilters) {
        //     console.log("Found saved filter preferences, applying them...");
        //     data.preferences.marketplace.typeFilters.forEach((savedFilter) => {
        //       const filter = typeFilters.value.find(
        //         (f) => f.type === savedFilter.type
        //       );
        //       if (filter) {
        //         filter.active = savedFilter.active;
        //       }
        //     });
        //   } else {
        //     console.log(
        //       "No saved filter preferences found, keeping initial filter"
        //     );
        //   }

        //   // Set other saved preferences
        //   if (data.preferences?.marketplace?.sortOrder) {
        //     localSortOrder.value = data.preferences.marketplace.sortOrder;
        //   }

        //   if (data.preferences?.marketplace?.selectedCategory) {
        //     selectedCategory.value =
        //       data.preferences.marketplace.selectedCategory;
        //     emit("update:selectedCategory", selectedCategory.value);
        //   }
        // } else {
        //   console.log(
        //     "No user preferences document found, keeping initial filter"
        //   );
        // }
      }

      // Always initialize and emit the filter types
      updateFilterTypeEmit();

    });

    return {
      localSearchQuery,
      localSortOrder,
      handleLocationFilterUpdate,
      typeFilters,
      toggleTypeFilter,
      localOrgMode,
      onOrgToggle,
      currentOrgUID,
      showClosedEconomyModal,
      onBlockedGlobalAttempt,
      postWrapperRef,
      panelRef,
      panelDirectionClass,
      panelDirection,
      showWishlist,
      toggleWishlist,
      iconsetPath,
      filtersVisible,
      searchActive,
      searchFocused,
      searchCollapsed,
      onSearchFocus,
      onSearchBlur,
      onPostActionClick,
    };
  },
};
</script>

<style scoped>
.filters-main-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--space-3);
  flex: 1; /* Allow to grow */
  min-width: 0;
}

.search-container {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 100px; /* reduced min-width to fit better on small screens */
  flex: 1; /* Grow to fill space */
}

.search-container input {
  width: 100%;
  height: var(--control-height-default);
  padding: var(--space-3);
  padding-left: calc(var(--control-height-default) * 0.9);
  padding-right: calc(var(--control-height-default) + var(--space-2));
  border-radius: var(--radius-sm);
}

.search-container .search-icon {
  position: absolute;
  left: var(--space-3);
  pointer-events: none;
  display: flex;
  align-items: center;
  opacity: 0.6;
}

/* Filter toggle button inside the search input */
.filter-toggle-btn {
  position: absolute;
  right: var(--space-2);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none !important;
  box-shadow: none !important;
  padding: var(--space-2) !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.45;
  transition: opacity 0.18s ease, transform 0.18s ease;
  border-radius: var(--radius-sm);
}
.filter-toggle-btn:hover {
  opacity: 0.7;
  transform: translateY(-50%);
}
.filter-toggle-btn.active {
  opacity: 1;
}

/* Filter controls — hidden by default, animated reveal */
.filter-controls {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  /* Desktop: slide in from left */
  max-width: 0;
  opacity: 0;
  transition: max-width 0.3s ease, opacity 0.25s ease, padding 0.3s ease;
  padding-left: 0;
  padding-right: 0;
}
.filter-controls.visible {
  max-width: 600px; /* generous upper bound */
  opacity: 1;
  padding-left: 0;
  padding-right: 0;
}

.type-filters-container {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.location-filter-component {
  position: relative;
}

.location-filter-inline-wrapper {
  display: flex;
  align-items: center;
}

.type-filter-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.2s ease;
  opacity: 0.5;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
}

.type-filter-item.active {
  opacity: 1;
}

.icon-container {
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-surface-raised);
  height: var(--control-height-default);
  width: var(--control-height-default);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease;
}

.type-filter-item:hover {
  opacity: 0.8;
}
.type-filter-item.active .icon-container {
  box-shadow: var(--shadow-surface-recessed-compact);
}

.filter-type-icon {
  height: var(--control-height-dense);
  width: var(--control-height-dense);
}

input {
  height: var(--control-height-default);
  padding: var(--space-3);
}

/* Wishlist icon button styles */
.favourites-icon-toggle {
  align-items: center;
}
.wishlistIconButton {
  background: var(--color-surface-canvas);
  border: 1px solid var(--color-border-control) !important;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-surface-raised);
  height: var(--control-height-default);
  width: var(--control-height-default);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  cursor: pointer;
  transition: box-shadow 0.18s ease, opacity 0.18s ease;
}
.wishlistIconButton:hover {
  opacity: 0.8;
}
.wishlistIconButton.active,
.wishlistIconButton:active {
  box-shadow: var(--shadow-surface-recessed-compact);
  opacity: 1;
}

/* Mobile: two rows, toolbar pinned at bottom */
@media (max-width: 768px) {
  /* Search shares row with buttons/actions */
  .search-container {
    flex: 1;
    min-width: 0;
    width: auto;
  }

  .marketToolBar {
    position: fixed;
    bottom: 0;
    left: 0;
    width: calc(100%);
    z-index: 5;
    background: var(--color-surface-canvas);
    margin: 0px;
    margin-bottom: -1px;
    padding: var(--space-4);
    box-shadow: var(--shadow-surface-floating);
  }

  .filters-top-row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    column-gap: var(--space-2);
    row-gap: var(--space-3);
    align-items: center;
  }

  /* Search fills left of row 1 */
  .filters-main-row {
    align-items: center;
    min-width: 0;
    overflow: hidden;
    grid-column: 1;
    grid-row: 1;
  }

  /* Buttons sit on right of row 1 */
  .postActionSlotWrapper {
    grid-column: 2;
    grid-row: 1;
    white-space: nowrap;
  }

  /* Filter icons span full row 2 — slide up from bottom on mobile */
  .filter-controls {
    grid-column: 1 / -1;
    grid-row: 2;
    gap: var(--space-2);
    justify-content: flex-start;
    /* Override desktop left-slide with bottom-slide */
    max-width: none;
    max-height: 0;
    opacity: 0;
    transition: max-height 0.3s ease, opacity 0.25s ease;
  }
  .filter-controls.visible {
    max-width: none;
    max-height: 100px; /* generous upper bound */
    opacity: 1;
  }

  .type-filters-container {
    gap: var(--space-2);
  }
}
</style>
<style scoped>
/* Slot wrapper for injected Post button (and optional selling action panel) */
.postActionSlotWrapper {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  position: relative;
}

/* Mobile specific adjustments can be added here if needed */

/* Ensure main toolbar and top row share horizontal space */
.marketToolBar {
  width: 100%;
  overflow: visible;
}
.filters-top-row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  align-items: center;
  gap: var(--space-3);
}
.filters-top-row > .filters-main-row {
  flex: 1 1 auto;
  min-width: 0; /* allow shrinking */
}
.marketToolBar.column.smallerGap {
  display: flex;
  flex-direction: column;
}
.postActionSlotWrapper button {
  white-space: nowrap;

}

/* Mobile: override flex with grid so search + buttons stay on row 1 */
@media (max-width: 768px) {
  .filters-top-row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    column-gap: var(--space-2);
    row-gap: var(--space-3);
  }
  .filters-top-row > .filters-main-row {
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
    overflow: hidden;
  }
  .filters-top-row > .filter-controls {
    grid-column: 1 / -1;
    grid-row: 2;
    /* Mobile: override desktop left-slide with bottom-slide */
    max-width: none;
    max-height: 0;
    opacity: 0;
    transition: max-height 0.3s ease, opacity 0.25s ease;
  }
  .filters-top-row > .filter-controls.visible {
    max-width: none;
    max-height: 100px;
    opacity: 1;
  }
}

/* Selling Action Panel directional behaviour */
/* Base panel styling */
.sellingActionPanel {
  position: absolute;
  right: 0;
  top: auto;
  bottom: auto;
  display: inline-block; /* let child size it */
  max-width: min(96vw, 520px);
  overflow: visible; /* child handles internal scroll */
  z-index: 40;
  animation: panelFadeIn 0.18s ease;
}

.sellingActionPanel.hidden {
  pointer-events: none;
  position: absolute;
  width: 0;
  height: 0;
  overflow: visible;
  animation: none;
}

.sellingActionPanel.hidden > * {
  pointer-events: auto;
}

/* Direction variants */
.sellingActionPanel.direction-down {
  top: calc(100% + 6px);
  bottom: auto;
  transform-origin: top right;
}
.sellingActionPanel.direction-up {
  bottom: calc(100% + 6px);
  top: auto;
  transform-origin: bottom right;
}

@media (max-width: 480px) {
  .sellingActionPanel {
    max-width: 92vw;
  }
  .filters-top-row {
    padding-bottom: var(--space-3);
  }
}

@media (max-width: 768px) {
  .sellingActionPanel.direction-up {
    bottom: calc(100% + 8px);
  }
  .sellingActionPanel.direction-down {
    top: calc(100% + 8px);
  }
}

@keyframes panelFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>

<!-- Unscoped: targets slotted post actions from parent (Ecosystem.vue) -->
<style>
/* When search is active on mobile, collapse post-action labels to icon-only */
@media (max-width: 768px) {
  .filters-top-row.search-active .postActionSlotWrapper .ecosystem-post-action {
    padding-left: var(--space-2) !important;
    padding-right: var(--space-2) !important;
    min-width: var(--control-height-default);
    justify-content: center;
    overflow: hidden;
    transition: padding 0.25s ease, min-width 0.25s ease;
  }
  .filters-top-row.search-active .postActionSlotWrapper .ecosystem-post-action svg {
    flex-shrink: 0;
  }
  /* Hide the text labels smoothly using width + opacity */
  .filters-top-row .postActionSlotWrapper .ecosystem-post-action .postBtnLabel {
    display: inline-block;
    max-width: 80px;
    opacity: 1;
    overflow: hidden;
    white-space: nowrap;
    transition: max-width 0.25s ease, opacity 0.2s ease, margin 0.25s ease;
    margin-left: var(--space-2);
  }
  .filters-top-row.search-active .postActionSlotWrapper .ecosystem-post-action .postBtnLabel {
    max-width: 0;
    opacity: 0;
    margin-left: 0;
  }
}
</style>
